import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronDown, ExternalLink, Eye, RefreshCw } from 'lucide-react';
import { apiGet, apiPost } from '../data/api';
import t from '../i18n/it';

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">
      {t.common.tennis}
    </span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">
      {t.common.table_tennis}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-400',
    won: 'bg-blue-500/15 text-blue-400',
    lost: 'bg-red-500/15 text-red-400',
  };
  const labels = { active: t.status.active, won: t.status.won, lost: t.status.lost };
  return (
    <span className={`px-1.5 py-0.5 text-xs rounded ${map[status] || 'bg-zinc-500/15 text-zinc-400'}`}>
      {labels[status] || status}
    </span>
  );
}

function EvBar({ ev }) {
  const pct = Math.min((ev / 0.40) * 100, 100);
  const color = ev >= 0.15 ? '#10B981' : ev >= 0.08 ? '#F59E0B' : '#71717A';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-zinc-800">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span
        className="text-sm tabular-nums text-zinc-300"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        +{(ev * 100).toFixed(1)}%
      </span>
    </div>
  );
}

function WatchLiveBadge({ flag }) {
  if (!flag) return <span className="text-zinc-700 text-xs">—</span>;
  return (
    <span className="flex items-center gap-1 text-xs text-amber-400">
      <Eye size={11} /> Si
    </span>
  );
}

export default function PrematchSignals() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sportFilter, setSportFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const fetchSignals = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (sportFilter) params.append('sport', sportFilter);
    if (statusFilter) params.append('status', statusFilter);
    const query = params.toString() ? `?${params}` : '';
    apiGet(`/prematch-signals${query}`)
      .then(setSignals)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSignals();
  }, [sportFilter, statusFilter]);

  const runEngine = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const result = await apiPost('/engine/prematch/run');
      setRunResult(result);
      fetchSignals();
    } catch (e) {
      setRunResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1
            className="text-4xl font-bold text-zinc-50 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t.signals.prematch_title}
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">{t.signals.prematch_subtitle}</p>
        </div>
        <button
          data-testid="run-prematch-engine-btn"
          onClick={runEngine}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors disabled:opacity-50"
          style={{ background: '#10B981', color: '#000' }}
          onMouseEnter={(e) => !running && (e.currentTarget.style.background = '#059669')}
          onMouseLeave={(e) => !running && (e.currentTarget.style.background = '#10B981')}
        >
          {running ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          {running ? t.signals.running : t.signals.run_prematch}
        </button>
      </div>

      {/* Engine run result */}
      {runResult && !runResult.error && (
        <div
          data-testid="engine-run-result"
          className="flex items-center gap-4 px-4 py-3 rounded-sm text-sm"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}
        >
          <span className="text-emerald-400 font-medium">{t.signals.engine_complete}</span>
          <span className="text-zinc-400">
            {runResult.matches_analyzed} {t.signals.matches_analyzed}
          </span>
          <span className="text-zinc-400">
            {runResult.signals_created} {t.signals.new_signals}
          </span>
          <span className="text-zinc-400">
            {runResult.signals_updated} {t.signals.updated}
          </span>
          <span
            className="text-zinc-500 ml-auto"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }}
          >
            {runResult.duration_ms}ms
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          data-testid="filter-sport"
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-sm text-zinc-300 transition-colors focus:outline-none focus:ring-1 focus:ring-emerald-500"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <option value="">{t.signals.all_sports}</option>
          <option value="tennis">{t.common.tennis}</option>
          <option value="table_tennis">{t.common.table_tennis}</option>
        </select>
        <select
          data-testid="filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-sm text-zinc-300 transition-colors focus:outline-none focus:ring-1 focus:ring-emerald-500"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <option value="">{t.signals.all_status}</option>
          <option value="active">{t.status.active}</option>
          <option value="won">{t.status.won}</option>
          <option value="lost">{t.status.lost}</option>
        </select>
        <span className="ml-auto text-xs text-zinc-500">
          {signals.length} {t.signals.total_signals}
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-sm overflow-hidden"
        style={{ background: '#18181B', border: '1px solid #27272A' }}
      >
        {loading ? (
          <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
            <Loader2 size={18} className="animate-spin" />
            <span>{t.common.loading}</span>
          </div>
        ) : error ? (
          <div className="text-red-400 text-center py-10">{t.common.error}</div>
        ) : signals.length === 0 ? (
          <div className="text-zinc-600 text-center py-16 text-sm">{t.signals.no_signals}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #27272A' }}>
                  {[
                    t.signals.match,
                    t.signals.sport,
                    t.signals.market,
                    t.signals.selection,
                    t.signals.bookmaker,
                    t.signals.odds_bk,
                    t.signals.odds_fair,
                    t.signals.ev_pct,
                    t.signals.confidence,
                    t.signals.watch_live,
                    t.signals.status,
                    '',
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`py-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-medium whitespace-nowrap ${
                        i === 0 ? 'text-left px-5' : i >= 5 && i <= 8 ? 'text-right px-4' : 'text-left px-4'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signals.map((sig) => (
                  <React.Fragment key={sig.id}>
                    <tr
                      data-testid={`signal-row-${sig.id}`}
                      className="transition-colors cursor-pointer"
                      style={{
                        borderBottom: expandedId === sig.id ? 'none' : '1px solid #1F1F22',
                        background: expandedId === sig.id ? 'rgba(39,39,42,0.4)' : 'transparent',
                      }}
                      onClick={() => setExpandedId(expandedId === sig.id ? null : sig.id)}
                      onMouseEnter={(e) =>
                        expandedId !== sig.id &&
                        (e.currentTarget.style.background = 'rgba(39,39,42,0.3)')
                      }
                      onMouseLeave={(e) =>
                        expandedId !== sig.id &&
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <td className="px-5 py-3 font-medium text-zinc-200 whitespace-nowrap">
                        {sig.match_name}
                      </td>
                      <td className="px-4 py-3">
                        <SportBadge sport={sig.sport} />
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{sig.market_type}</td>
                      <td className="px-4 py-3 text-zinc-300 font-medium">{sig.selection_name}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">{sig.bookmaker_name}</td>
                      <td
                        className="px-4 py-3 text-right text-emerald-400"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {sig.bookmaker_odds?.toFixed(2)}
                      </td>
                      <td
                        className="px-4 py-3 text-right text-zinc-500"
                        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                      >
                        {sig.fair_odds?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <EvBar ev={sig.expected_value} />
                      </td>
                      <td
                        className="px-4 py-3 text-right text-zinc-400"
                        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                      >
                        {(sig.confidence_score * 100).toFixed(0)}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        <WatchLiveBadge flag={sig.watch_live_flag} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={sig.signal_status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronDown
                          size={14}
                          className={`text-zinc-600 ml-auto transition-transform ${
                            expandedId === sig.id ? 'rotate-180' : ''
                          }`}
                        />
                      </td>
                    </tr>
                    {expandedId === sig.id && (
                      <tr style={{ borderBottom: '1px solid #1F1F22' }}>
                        <td
                          colSpan={12}
                          className="px-5 py-4"
                          style={{ background: 'rgba(16,185,129,0.04)' }}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-4 text-xs">
                                <span className="text-zinc-500">
                                  {t.signals.estimated_prob}:{' '}
                                  <span className="text-zinc-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                    {(sig.estimated_probability * 100).toFixed(1)}%
                                  </span>
                                </span>
                                <span className="text-zinc-500">
                                  {t.signals.ev}:{' '}
                                  <span className="text-emerald-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                    +{(sig.expected_value * 100).toFixed(2)}%
                                  </span>
                                </span>
                                <span className="text-zinc-500">
                                  {t.signals.fair_label}:{' '}
                                  <span className="text-zinc-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                    {sig.fair_odds?.toFixed(3)}
                                  </span>
                                </span>
                                {sig.watch_live_flag && (
                                  <span className="flex items-center gap-1 text-amber-400">
                                    <Eye size={11} /> {t.signals.follow_live}
                                  </span>
                                )}
                              </div>
                              {sig.id?.startsWith('pe_') && (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-500/60 bg-emerald-500/5 px-2 py-0.5 rounded">
                                  {t.signals.generated_by_prematch}
                                </span>
                              )}
                            </div>
                            <Link
                              to={`/incontri/${sig.match_id}`}
                              data-testid={`signal-match-link-${sig.id}`}
                              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 whitespace-nowrap flex-shrink-0 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t.common.view_match} <ExternalLink size={11} />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
